import styled, { css } from 'styled-components';
import { colors } from '@config/colors';
import { spacing } from '@config/spacing';
import { typography } from '@config/typography';
import { media } from '@config/breakpoints';

const PAGE_MAX_WIDTH = '1280px';
const PAGE_MAX_WIDTH_XL = '1480px';
const PAGE_MAX_WIDTH_3XL = '1680px';

/* The sticky nav floats over the top of every section below the hero, so a
   section's words start below the nav's foot (--nav-foot, measured by the
   landing page). Landing on a section's top, as the autoplay and the page
   dots do, then never hides a first line under the nav, however tall the
   section. */
const CLEAR_OF_NAV = `calc(var(--nav-foot, 70px) + ${spacing.xl})`;

export const BentoSection = styled.section<{ $variant?: 'default' | 'bare' | 'hero' }>`
  position: relative;
  z-index: 1;
  /* Each section fills the viewport so there's no "peekaboo" where the next
     section starts to bleed in above the fold. The small viewport (svh),
     where the browser knows it: on a phone, 100vh is the screen with the
     browser's bars hidden, so a section that fits it can still run under
     the address bar and the toolbar — and under the Play control, which
     sits above them — while they show. */
  min-height: 100vh;
  min-height: 100svh;
  display: flex;
  flex-direction: column;
  justify-content: center;

  padding: ${CLEAR_OF_NAV} ${spacing.lg} ${spacing['3xl']};

  ${media.md} {
    padding: ${CLEAR_OF_NAV} ${spacing['3xl']} ${spacing['4xl']};
  }

  ${media.lg} {
    padding: ${CLEAR_OF_NAV} ${spacing['5xl']} ${spacing['5xl']};
  }

  ${media.xl} {
    padding: ${CLEAR_OF_NAV} ${spacing['6xl']} ${spacing['5xl']};
  }

  /* The hero is words alone, centred in the first screen. The nav sits
     in the flow above this section, so the section starts a nav's height
     down; the deeper foot pulls the words back up to the screen's centre
     and keeps the Pause control clear of the buttons. */
  ${({ $variant }) =>
    $variant === 'hero' &&
    css`
      && {
        padding-top: 0;
        padding-bottom: ${spacing['8xl']};
      }
    `}
`;

export const BentoContainer = styled.div`
  max-width: ${PAGE_MAX_WIDTH};
  margin: 0 auto;
  width: 100%;

  ${media.xl} { max-width: ${PAGE_MAX_WIDTH_XL}; }
  ${media['3xl']} { max-width: ${PAGE_MAX_WIDTH_3XL}; }
`;

/* One eyebrow for every section: the redesigned sections' deep seafoam,
   the rung that reads at AA as small text on the water. */
export const BentoEyebrow = styled.div`
  font-family: ${typography.fontFamily.small};
  font-size: ${typography.fontSize.xs};
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${colors.brand.exxxtraDark};
  margin-bottom: ${spacing.lg};
`;

export const BentoHeading = styled.h2`
  font-family: 'Poppins', ${typography.fontFamily.heading};
  font-size: clamp(2rem, 4.6vw, 3.75rem);
  font-weight: 300;
  line-height: 1.1;
  letter-spacing: -0.02em;
  color: ${colors.neutral.extraDark};
  margin: 0 0 ${spacing.md};
`;
